import includes from 'lodash-es/includes'
import omit from 'lodash-es/omit'

import { EMPTY_LIST, EMPTY_OBJECT } from '../../../constants/common'
import { filterVisibleItems } from '../../../utils/common'
import { toBuildingGroupSelectItem } from '../../BuildingGroup'
import { toUserInfoStatusSelectItems } from '../../User'
import {
  BUILDING_REQUIRED_AUDIENCE,
  NEWSBOARD_POST_AUDIENCE_TO_ALL_COLLEAGUES,
  NEWSBOARD_POST_AUDIENCE_TO_DIGITAL_BOARD,
  NEWSBOARD_POST_AUDIENCE_TO_NOT_SIGNED_UP_DWELLERS,
  NEWSBOARD_POST_AUDIENCE_TO_NOT_SIGNED_UP_LANDLORDS,
  NEWSBOARD_POST_AUDIENCE_TO_SIGNED_UP_DWELLERS,
  NEWSBOARD_POST_AUDIENCE_TO_SIGNED_UP_LANDLORDS,
} from '../Newsboard.constants'
import {
  ALL_BUILDING_GROUPS_COUNT,
  ALL_BUILDINGS_COUNT,
  ALL_BUILDINGS_OPTION,
  NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_ALL_EMPLOYEES,
  NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_BUILDING,
  NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_BUILDING_GROUP,
  NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_NON_REGISTERED_USERS,
  NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_NON_REGISTERED_USERS_CHECKBOX,
  NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_RADIO_BUILDINGS,
  NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_REGISTERED_USERS,
  NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_REGISTERED_USERS_CHECKBOX,
  NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_TV_SCREENS,
  NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_USER_INFO_STATUS,
  NEWSBOARD_POST_FORM_RECIPIENTS_INITIAL_VALUES,
  NON_REGISTERED_USERS_OPTIONS,
  parseSubmitValues,
  RecipientAudienceOptionValue,
  REGISTERED_USERS_OPTIONS,
} from '../NewsboardPostForm'

import {
  RECIPIENT_ALL_BUILDINGS_ICON,
  RECIPIENT_TV_ICON,
  RECIPIENT_USERS_ICON,
} from './NewsboardPostProfileWidgetAudience.constants'

// Post profile `building_objs` carry the address in `address_obj.value` and use
// `id` (not `value`), so the shared building select-item mappers don't fit.
function toAudienceBuildingSelectItem(building) {
  return {
    ...(building.avatar ? { avatar: { src: building.avatar } } : null),
    label: building.address_obj?.value,
    value: building.id,
  }
}

function isAllBuildingsAudience({ audience, hasBuildingsOrGroups }) {
  return (
    !hasBuildingsOrGroups &&
    audience.some(item => BUILDING_REQUIRED_AUDIENCE.includes(item))
  )
}

function getAllBuildingsSelectItem(t) {
  return {
    label: t('Newsboard.ToAllBuildingsKey'),
    value: ALL_BUILDINGS_OPTION,
  }
}

// "All buildings" is signalled to the backend as an empty buildings list —
// both for the untouched "All buildings" tag and when every building is
// selected manually (mirrors getNewsboardPostFormPayload on creation).
function isAllBuildingsSelection(buildingIds, allBuildingsCount) {
  return (
    includes(buildingIds, ALL_BUILDINGS_OPTION) ||
    (buildingIds?.length > 0 && buildingIds.length === allBuildingsCount)
  )
}

function getRegisteredUsersOption(audience) {
  const hasLandlords = includes(
    audience,
    NEWSBOARD_POST_AUDIENCE_TO_SIGNED_UP_LANDLORDS
  )
  const hasDwellers = includes(
    audience,
    NEWSBOARD_POST_AUDIENCE_TO_SIGNED_UP_DWELLERS
  )

  if (hasLandlords && hasDwellers) {
    return REGISTERED_USERS_OPTIONS[0]
  }

  const value = hasLandlords
    ? NEWSBOARD_POST_AUDIENCE_TO_SIGNED_UP_LANDLORDS
    : NEWSBOARD_POST_AUDIENCE_TO_SIGNED_UP_DWELLERS

  return REGISTERED_USERS_OPTIONS.find(option => option.value === value)
}

function getNonRegisteredUsersOption(audience) {
  const hasLandlords = includes(
    audience,
    NEWSBOARD_POST_AUDIENCE_TO_NOT_SIGNED_UP_LANDLORDS
  )
  const hasDwellers = includes(
    audience,
    NEWSBOARD_POST_AUDIENCE_TO_NOT_SIGNED_UP_DWELLERS
  )

  if (hasLandlords && hasDwellers) {
    return NON_REGISTERED_USERS_OPTIONS[0]
  }

  const value = hasLandlords
    ? NEWSBOARD_POST_AUDIENCE_TO_NOT_SIGNED_UP_LANDLORDS
    : NEWSBOARD_POST_AUDIENCE_TO_NOT_SIGNED_UP_DWELLERS

  return NON_REGISTERED_USERS_OPTIONS.find(option => option.value === value)
}

export function getPostHasBuildingsOrGroups(postData) {
  return (
    postData?.building_objs?.length > 0 ||
    postData?.building_group_objs?.length > 0
  )
}

// Reconstruct the grouped recipient lines shown in the widget from the post's
// flat `new_audience` enum list — the inverse of how the recipients form
// expands its "all / landlords only / tenants only" selections. Reuses the
// existing (already translated) option/tag label keys.
export function getPostAudienceRecipientItems({
  audience = EMPTY_LIST,
  hasBuildingsOrGroups,
  isStaff,
  t,
}) {
  if (!isStaff) {
    return EMPTY_LIST
  }

  const hasRegisteredLandlords = includes(
    audience,
    NEWSBOARD_POST_AUDIENCE_TO_SIGNED_UP_LANDLORDS
  )
  const hasRegisteredDwellers = includes(
    audience,
    NEWSBOARD_POST_AUDIENCE_TO_SIGNED_UP_DWELLERS
  )
  const hasNonRegisteredLandlords = includes(
    audience,
    NEWSBOARD_POST_AUDIENCE_TO_NOT_SIGNED_UP_LANDLORDS
  )
  const hasNonRegisteredDwellers = includes(
    audience,
    NEWSBOARD_POST_AUDIENCE_TO_NOT_SIGNED_UP_DWELLERS
  )

  const items = [
    {
      Icon: RECIPIENT_USERS_ICON,
      id: 'registered',
      isVisible: hasRegisteredLandlords || hasRegisteredDwellers,
      text: t(
        hasRegisteredLandlords && hasRegisteredDwellers
          ? 'Newsboard.AllRegisteredUsersKey'
          : hasRegisteredLandlords
            ? 'Newsboard.RegisteredLandlordsOnlyKey'
            : 'Newsboard.RegisteredTenantsOnlyKey'
      ),
    },
    {
      Icon: RECIPIENT_USERS_ICON,
      id: 'non_registered',
      isVisible: hasNonRegisteredLandlords || hasNonRegisteredDwellers,
      text: t(
        hasNonRegisteredLandlords && hasNonRegisteredDwellers
          ? 'Newsboard.AllNonRegisteredUsersKey'
          : hasNonRegisteredLandlords
            ? 'Newsboard.NonRegisteredLandlordsOnlyKey'
            : 'Newsboard.NonRegisteredTenantsOnlyKey'
      ),
    },
    {
      Icon: RECIPIENT_TV_ICON,
      id: 'tv_screens',
      isVisible: includes(audience, NEWSBOARD_POST_AUDIENCE_TO_DIGITAL_BOARD),
      text: t('Newsboard.TvScreensTagKey'),
    },
    {
      Icon: RECIPIENT_USERS_ICON,
      id: 'all_employees',
      isVisible: includes(audience, NEWSBOARD_POST_AUDIENCE_TO_ALL_COLLEAGUES),
      text: t('Newsboard.AllEmployeesTagKey'),
    },
    {
      Icon: RECIPIENT_ALL_BUILDINGS_ICON,
      id: 'all_buildings',
      isVisible: isAllBuildingsAudience({ audience, hasBuildingsOrGroups }),
      text: t('Newsboard.ToAllBuildingsKey'),
    },
  ]

  return filterVisibleItems(items)
}

export function getAudienceRecipientItemProps(value) {
  return {
    ListIcon: value.Icon,
    text: value.text,
  }
}

export function getAudienceUserStatusItemProps({ value }) {
  return {
    ListIcon: RECIPIENT_USERS_ICON,
    text: value,
  }
}

export function getAudienceBuildingItemProps(value) {
  return {
    avatar: value.photo_obj?.preview ?? value.avatar,
    text: value.address_obj?.value,
    value: value.id,
  }
}

export function getAudienceBuildingGroupItemProps(value) {
  return {
    avatar: value.avatar_obj?.preview ?? value.avatar,
    text: value.name,
    value: value.id,
  }
}

export function getAudienceModalInitialValues(postData, t) {
  const audience = postData?.new_audience || EMPTY_LIST
  const buildings = postData?.building_objs || EMPTY_LIST
  const buildingGroups = postData?.building_group_objs || EMPTY_LIST

  const hasRegistered =
    includes(audience, NEWSBOARD_POST_AUDIENCE_TO_SIGNED_UP_LANDLORDS) ||
    includes(audience, NEWSBOARD_POST_AUDIENCE_TO_SIGNED_UP_DWELLERS)

  const hasNonRegistered =
    includes(audience, NEWSBOARD_POST_AUDIENCE_TO_NOT_SIGNED_UP_LANDLORDS) ||
    includes(audience, NEWSBOARD_POST_AUDIENCE_TO_NOT_SIGNED_UP_DWELLERS)

  const isAllBuildings = isAllBuildingsAudience({
    audience,
    hasBuildingsOrGroups: getPostHasBuildingsOrGroups(postData),
  })

  return {
    ...NEWSBOARD_POST_FORM_RECIPIENTS_INITIAL_VALUES,
    [NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_ALL_EMPLOYEES]: includes(
      audience,
      NEWSBOARD_POST_AUDIENCE_TO_ALL_COLLEAGUES
    ),
    [NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_BUILDING]: isAllBuildings
      ? [getAllBuildingsSelectItem(t)]
      : buildings.map(toAudienceBuildingSelectItem),
    [NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_BUILDING_GROUP]:
      buildingGroups.map(toBuildingGroupSelectItem),
    [NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_NON_REGISTERED_USERS]:
      hasNonRegistered
        ? getNonRegisteredUsersOption(audience)
        : NON_REGISTERED_USERS_OPTIONS[0],
    [NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_NON_REGISTERED_USERS_CHECKBOX]:
      hasNonRegistered,
    [NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_RADIO_BUILDINGS]:
      buildingGroups.length > 0
        ? RecipientAudienceOptionValue.BuildingGroups
        : RecipientAudienceOptionValue.Building,
    [NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_REGISTERED_USERS]: hasRegistered
      ? getRegisteredUsersOption(audience)
      : REGISTERED_USERS_OPTIONS[0],
    [NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_REGISTERED_USERS_CHECKBOX]:
      hasRegistered,
    [NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_TV_SCREENS]: includes(
      audience,
      NEWSBOARD_POST_AUDIENCE_TO_DIGITAL_BOARD
    ),
    [NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_USER_INFO_STATUS]:
      toUserInfoStatusSelectItems(
        postData?.user_info_status_objs || EMPTY_LIST
      ),
  }
}

export function getAudiencePayload(
  values,
  { isAdmin, isStaff } = EMPTY_OBJECT
) {
  const recipient = parseSubmitValues(values)

  const payload = omit(recipient, [
    ALL_BUILDINGS_COUNT,
    ALL_BUILDING_GROUPS_COUNT,
  ])

  // Dwellers can only edit buildings — sending the admin-only audience fields
  // (building groups, user statuses) is rejected with 403. Drop them so a
  // dweller editing a post that already targets either one doesn't re-send it;
  // omitting the key leaves the backend's previously saved value untouched.
  if (!isStaff) {
    return omit(payload, [
      NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_BUILDING_GROUP,
      NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_USER_INFO_STATUS,
    ])
  }

  // A post targets either buildings or building groups. Always send BOTH keys
  // as arrays: an empty list clears the criterion the user removed or didn't
  // choose (omitting it makes the PATCH keep the previously saved value).
  if (
    values[NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_RADIO_BUILDINGS] ===
    RecipientAudienceOptionValue.BuildingGroups
  ) {
    payload[NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_BUILDING] = EMPTY_LIST
    payload[NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_BUILDING_GROUP] =
      payload[NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_BUILDING_GROUP] ??
      EMPTY_LIST
  } else {
    payload[NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_BUILDING_GROUP] =
      EMPTY_LIST

    const isAllBuildings = isAllBuildingsSelection(
      payload[NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_BUILDING],
      recipient[ALL_BUILDINGS_COUNT]
    )

    // "All buildings" is signalled to the backend as an empty buildings list
    // for admins only; managers and dwellers must send the explicit building
    // ids (the field already holds every building's id).
    payload[NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_BUILDING] =
      isAllBuildings && isAdmin
        ? EMPTY_LIST
        : (payload[NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_BUILDING] ??
          EMPTY_LIST)
  }

  payload[NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_USER_INFO_STATUS] = (
    values[NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_USER_INFO_STATUS] ??
    EMPTY_LIST
  ).map(item => item.value)

  return payload
}
